<template>
  <div class="metadata-column-modal">
    <div class="metadata-column-dialog">
      <div class="modal-header">
        <span>{{ $t('Manage Table Columns') }}</span>
        <span class="close-icon" @click="closeModal">×</span>
      </div>

      <div class="modal-body">
        <div class="metadata-column-search-wrap">
          <input
            v-model="searchText"
            type="text"
            class="metadata-column-search-input"
            :placeholder="$t('Search metadata fields')"
          />

          <span
            v-if="searchText"
            class="metadata-column-search-clear"
            @click="searchText = ''"
          >
            ×
          </span>
        </div>

        <div
          v-for="field in filteredTagMetadataFields"
          :key="field._id"
          class="configurable-table-row"
        >
          <div class="configurable-table-info">
            <span class="configurable-table-name" :title="field.name">
              {{ getCustomMetadataName(field) }}
            </span>
          </div>

          <label class="column-switch">
            <input
              type="checkbox"
              :checked="isEnabled(field._id)"
              @change="toggleField(field._id, $event.target.checked)"
            />
            <span class="column-slider"></span>
          </label>
        </div>

        <div v-if="!tagMetadataFields.length" class="empty-text">
          {{ $t('No metadata fields found') }}
        </div>

        <div v-else-if="!filteredTagMetadataFields.length" class="empty-text">
          {{ $t('No matching metadata fields found') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {NotifierConstants} from "../../components.constants.js";
import {mapMutations} from "vuex";

const STORAGE_KEY = "metadata_table_column_preferences";

export default {
  name: "ConfigurableTableColumns",

  data() {
    return {
      enabledFieldIds: [],
      searchText: ""
    };
  },

  computed: {
    allStartUpData() {
      return this.$store.state.startUpData.startUpDataDetails || {};
    },

    tagMetadataFields() {
      const customMetadatas = this.allStartUpData.custom_metadatas || [];

      return customMetadatas.filter(
        field => field && field._id && field.name
      );
    },

    filteredTagMetadataFields() {
      const searchValue = String(this.searchText || "")
        .toLowerCase()
        .trim();

      if (!searchValue) {
        return this.tagMetadataFields;
      }

      return this.tagMetadataFields.filter(field => {
        const originalName = String(field.name || "").toLowerCase();
        const displayName = String(
          this.getCustomMetadataName(field) || ""
        ).toLowerCase();

        return (
          originalName.includes(searchValue) ||
          displayName.includes(searchValue)
        );
      });
    }
  },

  mounted() {
    this.loadEnabledFields();
    this.registerForMetadataWebsocketNotifications();
  },

  beforeDestroy() {
    this.unregisterForMetadataWebsocketNotifications();
  },

  methods: {
    ...mapMutations("websocket",["addWSEventListener", "removeWSEventListener"]),
    getCustomMetadataName(fieldInfo) {
      if (!fieldInfo) {
        return "";
      }

      const defaultName = fieldInfo.name || "";
      const aliasMapper = Array.isArray(fieldInfo.language_alias_mapper)
        ? fieldInfo.language_alias_mapper
        : [];

      const currentLocale =
        this.$i18n && this.$i18n.locale
          ? String(this.$i18n.locale).toLowerCase()
          : "";

      if (!currentLocale || !aliasMapper.length) {
        return defaultName;
      }

      const baseLocale = currentLocale.split("-")[0];

      const matchedAlias = aliasMapper.find(mapper => {
        const mapperCode = String(
          (mapper && mapper.code) || ""
        ).toLowerCase();

        return (
          mapperCode === currentLocale ||
          mapperCode === baseLocale ||
          mapperCode.split("-")[0] === baseLocale
        );
      });

      if (
        matchedAlias &&
        typeof matchedAlias.field_alias === "string" &&
        matchedAlias.field_alias.trim()
      ) {
        return matchedAlias.field_alias.trim();
      }

      return defaultName;
    },

    loadEnabledFields() {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);
        const parsedData = storedData ? JSON.parse(storedData) : [];

        this.enabledFieldIds = Array.isArray(parsedData)
          ? parsedData
          : [];
      } catch (error) {
        this.enabledFieldIds = [];
      }
    },

    isEnabled(fieldId) {
      return this.enabledFieldIds.includes(fieldId);
    },

    toggleField(fieldId, checked) {
      if (checked) {
        if (!this.enabledFieldIds.includes(fieldId)) {
          this.enabledFieldIds.push(fieldId);
        }
      } else {
        this.enabledFieldIds = this.enabledFieldIds.filter(
          id => id !== fieldId
        );
      }

      this.updateColumns();
    },

    updateColumns() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(this.enabledFieldIds)
      );

      const updatedFieldIds = [...this.enabledFieldIds];

      this.$emit("columns-updated", updatedFieldIds);

      window.dispatchEvent(
        new CustomEvent("metadata-table-columns-updated", {
          detail: {
            fieldIds: updatedFieldIds,
            changeType: "selection",
            fieldId: null,
            refreshKey: Date.now()
          }
        })
      );
    },

    handleMetadataFieldNotification(notification) {
      const notificationData =
        notification &&
        notification.detail &&
        Object.keys(notification.detail).length
          ? notification.detail
          : notification;

      if (
        !notificationData ||
        !notificationData.message ||
        !notificationData.message_type
      ) {
        return;
      }

      const fieldInfo = notificationData.message;
      const fieldId = fieldInfo && fieldInfo._id;

      if (!fieldId) {
        return;
      }

      const messageType = notificationData.message_type;

      if (
        messageType === NotifierConstants.fieldCreatedNotification ||
        messageType === NotifierConstants.fieldModifiedNotification
      ) {
        this.$nextTick(() => {
          this.notifyConfiguredColumnsUpdated({
            changeType:
              messageType === NotifierConstants.fieldCreatedNotification
                ? "created"
                : "modified",
            fieldId
          });
        });

        return;
      }

      if (
        messageType === NotifierConstants.fieldRemovedNotification
      ) {
        const wasSelected = this.enabledFieldIds.includes(fieldId);

        if (wasSelected) {
          this.enabledFieldIds = this.enabledFieldIds.filter(
            id => id !== fieldId
          );

          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(this.enabledFieldIds)
          );

          if (this.$toast) {
            this.$toast.info(
              this.$t(
                "{fieldName} was deleted by another user",
                {
                  fieldName:
                    fieldInfo.name || this.$t("Selected metadata field")
                }
              )
            );
          }
        }

        this.notifyConfiguredColumnsUpdated({
          changeType: "removed",
          fieldId
        });
      }
    },

    notifyConfiguredColumnsUpdated(changeInfo = {}) {
      const updatedFieldIds = [...this.enabledFieldIds];

      this.$emit("columns-updated", updatedFieldIds);

      window.dispatchEvent(
        new CustomEvent("metadata-table-columns-updated", {
          detail: {
            fieldIds: updatedFieldIds,
            changeType: changeInfo.changeType || "selection",
            fieldId: changeInfo.fieldId || null,
            refreshKey: Date.now()
          }
        })
      );
    },

    registerForMetadataWebsocketNotifications() {
      if (typeof this.addWSEventListener !== "function") {
        return;
      }

      this.addWSEventListener({
        eventname: [
          NotifierConstants.fieldCreatedNotification,
          NotifierConstants.fieldModifiedNotification,
          NotifierConstants.fieldRemovedNotification
        ],
        callback: this.handleMetadataFieldNotification,
        parent: "ConfigurableTableColumns"
      });
    },

    unregisterForMetadataWebsocketNotifications() {
      if (typeof this.removeWSEventListener !== "function") {
        return;
      }

      this.removeWSEventListener({
        eventname: [
          NotifierConstants.fieldCreatedNotification,
          NotifierConstants.fieldModifiedNotification,
          NotifierConstants.fieldRemovedNotification
        ],
        callback: this.handleMetadataFieldNotification,
        parent: "ConfigurableTableColumns"
      });
    },

    closeModal() {
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.metadata-column-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.68);
  display: flex;
  align-items: center;
  justify-content: center;
}

.metadata-column-dialog {
  width: 500px;
  max-height: 590px;
  background: #15181b;
  border: 1px solid #4a4a4a;
  border-radius: 8px;
  color: #eee;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.65);
  overflow: hidden;
}

.modal-header {
  height: 48px;
  padding: 0 16px;
  background: #1a1e21;
  border-bottom: 1px solid #2b2b2b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #eee;
  font-size: 14px;
  font-weight: 600;
}

.close-icon {
  width: 26px;
  height: 26px;
  border-radius: 4px;
  color: #9c9c9c;
  cursor: pointer;
  font-size: 24px;
  line-height: 23px;
  text-align: center;
}

.close-icon:hover {
  color: #eee;
  background: #22272b;
}

.modal-body {
  max-height: 500px;
  padding: 8px 10px;
  overflow-y: auto;
  background: #1a1e21;
}

.metadata-column-search-wrap {
  position: relative;
  margin: 4px 0 8px 0;
  display:flex;
  width:60%;
}

.metadata-column-search-input {
  width: 100%;
  height: 34px;
  padding: 0 32px 0 10px;
  border: 1px solid #4a4a4a;
  border-radius: 5px;
  color: #eee;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}

.metadata-column-search-input:focus {
  border-color: #007bff;
}

.metadata-column-search-input::placeholder {
  color: #757575;
}

.metadata-column-search-clear {
  position: absolute;
  right: 9px;
  top: 4px;
  color: #9c9c9c;
  font-size: 20px;
  line-height: 24px;
  cursor: pointer;
}

.metadata-column-search-clear:hover {
  color: #eee;
}

.configurable-table-row {
  min-height: 46px;
  padding: 0 10px;
  border-bottom: 1px solid #212121;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.configurable-table-row:hover {
  background: #22272b;
}

.configurable-table-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: start;
}

.configurable-table-name {
  color: #eee;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.configurable-table-type {
  color: #757575;
  font-size: 11px;
  line-height: 14px;
  text-transform: uppercase;
}

.empty-text {
  padding: 32px 0;
  color: #757575;
  font-size: 13px;
  text-align: center;
}

.column-switch {
  position: relative;
  flex: 0 0 38px;
  width: 38px;
  height: 20px;
  margin: 0;
}

.column-switch input {
  display: none;
}

.column-slider {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background: #4a4a4a;
  border: 1px solid #5a5a5a;
  border-radius: 20px;
  transition: 0.2s ease;
}

.column-slider::before {
  content: "";
  position: absolute;
  width: 14px;
  height: 14px;
  left: 3px;
  top: 2px;
  background: #cfd3d7;
  border-radius: 50%;
  transition: 0.2s ease;
}

.column-switch input:checked + .column-slider {
  background: #007bff;
  border-color: #007bff;
}

.column-switch input:checked + .column-slider::before {
  transform: translateX(17px);
  background: #ffffff;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #1a1e21;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #333;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>